import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, HandHelping } from 'lucide-react';
import { Button } from '@/components/ui/button';

const actions = [
  {
    icon: Heart,
    title: "Make a Donation",
    description: "Your tax-deductible donation helps build, stabilize, and strengthen the local communities we serve.",
    cta: "Donate Now",
    href: "https://www.paypal.me/roseofjerichocd?locale.x=en_US",
    primary: true
  },
  {
    icon: Users,
    title: "Become a Volunteer",
    description: "Share your vision, time, and talents. Our volunteers are driven by collaboration and community engagement.",
    cta: "Join Our Team",
    href: "mailto:info@roseofjericho-cd.org?subject=Volunteer Inquiry",
    primary: false
  },
  {
    icon: HandHelping,
    title: "Partner With Us",
    description: "We cannot do this work alone. Partner with us to create lasting change in the communities we serve.",
    cta: "Get In Touch",
    href: "mailto:info@roseofjericho-cd.org?subject=Partnership Inquiry",
    primary: false
  }
];

export default function GetInvolved() {
  return (
    <section id="donate" className="py-24 sm:py-32 bg-primary text-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-secondary font-medium tracking-[0.2em] uppercase text-sm mb-4">Get Involved</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold">
            Be Part of the <span className="italic">Transformation</span>
          </h2>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto text-lg">
            Every contribution — whether financial, time, or partnership — moves us closer to thriving communities.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {actions.map((action, i) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-8 text-center hover:bg-white/15 transition-colors"
            >
              <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <action.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">{action.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">{action.description}</p>
              <a href={action.href} target="_blank" rel="noopener noreferrer">
                <Button
                  className={action.primary
                    ? "bg-secondary hover:bg-secondary/90 text-white rounded-full px-6"
                    : "bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6"
                  }
                >
                  {action.cta}
                </Button>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}